import React, { useState, useRef, useEffect } from 'react';
import { FaPencil, FaPlus, FaCircleInfo, FaTrash, FaAlignLeft, FaAlignCenter, FaAlignRight } from 'react-icons/fa6';
import './Attributes.css';

const PRESENCE = ['Shown - Value Required', 'Shown - Value Optional', 'Hidden'];
const AVAILABLE = ['All values available', 'Limit available values'];
const LIMITATION = ['Show and Allow Selection', 'Show Only', 'Hide'];

// strips html tags -> plain text (used to show the description in the list)
const toText = (html) => {
  const d = document.createElement('div');
  d.innerHTML = html || '';
  return (d.textContent || '').trim();
};

const Field = ({ label, required, info, value, onChange, options, placeholder }) => (
  <div className="attr-row">
    <label className="attr-label">
      {label}
      {required && <span className="attr-req">*</span>}
      {info && <FaCircleInfo className="attr-info" title={info} />}
    </label>
    <div className="attr-select-wrap">
      <select className="attr-select" value={value} onChange={(e) => onChange(e.target.value)}>
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </div>
  </div>
);

// ---------- Toolbar helpers ----------
const TB = ({ title, onClick, children }) => (
  <button type="button" className="attr-tb" title={title} onMouseDown={(e) => e.preventDefault()} onClick={onClick}>
    {children}
  </button>
);
const Sep = () => <span className="attr-tb-sep" />;

// ---------- Description modal (rich text editor) ----------
const DescriptionModal = ({ title, initialHtml, onCancel, onSave }) => {
  const editorRef = useRef(null);
  const sizeRef = useRef(3);

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = initialHtml || '';
      editorRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exec = (cmd, val = null) => {
    if (editorRef.current) editorRef.current.focus();
    document.execCommand(cmd, false, val);
  };

  const changeSize = (delta) => {
    sizeRef.current = Math.min(7, Math.max(1, sizeRef.current + delta));
    exec('fontSize', sizeRef.current);
  };

  const addLink = () => {
    const url = window.prompt('Enter link URL');
    if (url) exec('createLink', url);
  };

  const addImage = () => {
    const url = window.prompt('Enter image URL');
    if (url) exec('insertImage', url);
  };

  const addTable = () =>
    exec(
      'insertHTML',
      '<table border="1" style="border-collapse:collapse;width:100%"><tr><td>&nbsp;</td><td>&nbsp;</td></tr><tr><td>&nbsp;</td><td>&nbsp;</td></tr></table><p></p>'
    );

  const pastePlain = async () => {
    try {
      const text = await navigator.clipboard.readText();
      exec('insertText', text);
    } catch (e) {
      window.alert('Clipboard access is blocked. Please use Ctrl+V.');
    }
  };

  const printContent = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<html><head><title>${title}</title></head><body>${editorRef.current.innerHTML}</body></html>`);
    w.document.close();
    w.focus();
    w.print();
    w.close();
  };

  const handleSave = () => {
    const html = editorRef.current ? editorRef.current.innerHTML : '';
    onSave(toText(html) || html.includes('<img') ? html : '');
  };

  return (
    <div className="attr-modal-overlay">
      <div className="attr-modal">
        <div className="attr-modal-head">
          <span>{title}</span>
          <button type="button" className="attr-modal-close" onClick={onCancel}>&times;</button>
        </div>

        <div className="attr-modal-body">
          <div className="attr-modal-label">Specific Extended Description</div>

          <div className="attr-editor-box">
            <div className="attr-toolbar">
              <TB title="Bold" onClick={() => exec('bold')}><b>B</b></TB>
              <TB title="Italic" onClick={() => exec('italic')}><i>I</i></TB>
              <TB title="Underline" onClick={() => exec('underline')}><u>U</u></TB>
              <Sep />
              <TB title="Bullet list" onClick={() => exec('insertUnorderedList')}>•</TB>
              <TB title="Numbered list" onClick={() => exec('insertOrderedList')}>1.</TB>
              <TB title="Indent" onClick={() => exec('indent')}>☰</TB>
              <Sep />
              <TB title="Quote" onClick={() => exec('formatBlock', 'blockquote')}>“</TB>
              <Sep />
              <TB title="Align left" onClick={() => exec('justifyLeft')}><FaAlignLeft /></TB>
              <TB title="Align center" onClick={() => exec('justifyCenter')}><FaAlignCenter /></TB>
              <TB title="Align right" onClick={() => exec('justifyRight')}><FaAlignRight /></TB>
              <Sep />
              <TB title="Emoji" onClick={() => exec('insertText', '😊')}>☺</TB>
              <TB title="Link" onClick={addLink}>🔗</TB>
              <Sep />
              <TB title="Paste as plain text" onClick={pastePlain}>📋</TB>
              <TB title="Insert image" onClick={addImage}>🖼️</TB>
              <TB title="Insert table" onClick={addTable}>📊</TB>
              <Sep />
              <select
                className="attr-tb-select"
                defaultValue=""
                onChange={(e) => {
                  if (e.target.value) exec('fontSize', e.target.value);
                  e.target.value = '';
                }}
              >
                <option value="">Size</option>
                <option value="2">Small</option>
                <option value="3">Normal</option>
                <option value="5">Large</option>
                <option value="6">Huge</option>
              </select>
              <Sep />
              <TB title="Smaller text" onClick={() => changeSize(-1)}>A<sup>-</sup></TB>
              <TB title="Larger text" onClick={() => changeSize(1)}>A<sup>+</sup></TB>
            </div>

            <div className="attr-toolbar">
              <TB title="Clear all" onClick={() => { if (editorRef.current) editorRef.current.innerHTML = ''; }}>✕</TB>
              <TB title="Select all" onClick={() => exec('selectAll')}>📄</TB>
              <TB title="Copy" onClick={() => exec('copy')}>📋</TB>
              <TB title="Save" onClick={handleSave}>💾</TB>
              <TB title="Print" onClick={printContent}>🖨️</TB>
              <Sep />
              <TB title="Undo" onClick={() => exec('undo')}>↩</TB>
              <TB title="Redo" onClick={() => exec('redo')}>↪</TB>
              <Sep />
              <TB title="Clear formatting" onClick={() => exec('removeFormat')}><b>T<sub>x</sub></b></TB>
            </div>

            <div className="attr-editor" ref={editorRef} contentEditable suppressContentEditableWarning />
          </div>
        </div>

        <div className="attr-modal-foot">
          <button type="button" className="attr-modal-btn" onClick={onCancel}>Cancel</button>
          <button type="button" className="attr-modal-btn" onClick={handleSave}>Save</button>
        </div>
      </div>
    </div>
  );
};

// ---------- Main component ----------
export default function Attributes({ onSave }) {
  const idRef = useRef(0);
  const [collapsed, setCollapsed] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newGroups, setNewGroups] = useState([]); // rows added with "Add Group"
  const [groups, setGroups] = useState([
    { id: 1, name: 'Submission Method (2)', description: '', required: false, presence: PRESENCE[0], available: AVAILABLE[0], defaultValue: '', limitation: '' },
    { id: 2, name: 'Country of Manufacturing', description: '', required: true, presence: PRESENCE[0], available: AVAILABLE[0], defaultValue: '', limitation: LIMITATION[0] },
  ]);

  const updateGroup = (id, field, value) =>
    setGroups((prev) => prev.map((g) => (g.id === id ? { ...g, [field]: value } : g)));

  const handleAddGroup = () => {
    idRef.current += 1;
    setNewGroups((prev) => [...prev, { id: idRef.current, name: '' }]);
  };

  const updateNewGroup = (id, name) =>
    setNewGroups((prev) => prev.map((g) => (g.id === id ? { ...g, name } : g)));

  const removeNewGroup = (id) => setNewGroups((prev) => prev.filter((g) => g.id !== id));

  const editingGroup = groups.find((g) => g.id === editingId);

  return (
    <div className="attr-container">
      <div className="attr-header">
        <h2 className="attr-title">Attributes</h2>
        <div className="attr-header-actions">
          <button type="button" className="attr-btn" onClick={handleAddGroup}>
            <FaPlus /> Add Group
          </button>
          <button type="button" className="attr-btn" onClick={() => setCollapsed((c) => !c)}>
            {collapsed ? 'Expand' : 'Collapse'}
          </button>
        </div>
      </div>

      <div className="attr-panel">
        {/* Groups added with the "Add Group" button */}
        {newGroups.map((ng) => (
          <div className="attr-new" key={ng.id}>
            <span className="attr-new-label">Group</span>
            <input
              type="text"
              className="attr-new-input"
              placeholder="Enter group name"
              value={ng.name}
              onChange={(e) => updateNewGroup(ng.id, e.target.value)}
            />
            <button type="button" className="attr-trash" title="Delete group" onClick={() => removeNewGroup(ng.id)}>
              <FaTrash />
            </button>
          </div>
        ))}

        {groups.map((g) => {
          const text = toText(g.description);
          return (
            <div className="attr-group" key={g.id}>
              <h3 className="attr-group-title">{g.name}</h3>
              <div className="attr-desc">
                <strong>Specific Extended Description</strong>
                <button type="button" className="attr-link" onClick={() => setEditingId(g.id)}>
                  {text ? 'Edit' : 'Add'}
                </button>
                <em className="attr-desc-text">{text || 'No description added yet'}</em>
              </div>

              {!collapsed && (
                <>
                  <Field label="Presence" required={g.required} value={g.presence} options={PRESENCE}
                    onChange={(v) => updateGroup(g.id, 'presence', v)} />
                  <Field label="Available Values" value={g.available} options={AVAILABLE}
                    onChange={(v) => updateGroup(g.id, 'available', v)} />
                  <Field label="Default Value" value={g.defaultValue} options={[]} placeholder="Select Default"
                    onChange={(v) => updateGroup(g.id, 'defaultValue', v)} />
                  <Field label="Account Limitation" info="Limit what accounts can see or select" value={g.limitation}
                    options={LIMITATION} placeholder="Select Default"
                    onChange={(v) => updateGroup(g.id, 'limitation', v)} />
                </>
              )}
            </div>
          );
        })}
      </div>

      <button type="button" className="attr-save-btn" onClick={() => onSave && onSave(groups, newGroups)}>
        <FaPencil /> Save
      </button>

      {editingGroup && (
        <DescriptionModal
          title={editingGroup.name}
          initialHtml={editingGroup.description}
          onCancel={() => setEditingId(null)}
          onSave={(html) => {
            updateGroup(editingGroup.id, 'description', html);
            setEditingId(null);
          }}
        />
      )}
    </div>
  );
}